import React, { useMemo, useState } from "react";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";
import HeaderButtons from "components/Button/HeaderButtons";
import Button from "components/Button";
import Toggle from "components/Toggle";
import { fieldCls } from "components/InlineTable";
import { getApiErrorMessage } from "util/errorMessage";
import feedbackManager from "services/Feedback/feedbackManager";
import {
  Feedback,
  formatPeriod,
  formatPeriodWithMonths,
  getQuarterMonths,
  getSelectableQuarters,
  KraArea,
  MAX_ACHIEVEMENT_LENGTH,
  MAX_GOAL_ROWS,
  ManagerFeedbackRequest,
  parseQuarterKey,
  QuarterRef,
  quarterKey,
} from "services/Feedback/feedbackInterface";
import {
  canAddGoalRow,
  calculateTotalWeightage,
  createEmptyGoalRow,
  getObjectiveOptions,
  GoalRowDraft,
  toApproverRowDrafts,
  toGoalRowDrafts,
  toItemRequests,
  validateManagerRows,
} from "./feedbackFormUtils";
import {
  FIELD_ERROR_CLS,
  FIELD_LABEL_CLS,
  RatingSelect,
  WeightageMeter,
} from "./components/FeedbackBits";
import { KraAreaSelect, KraObjectiveSelect } from "./components/KraSelects";

/**
 * Manager / admin feedback form, in three modes.
 *
 *   approve → POST /employee with `selfFeedbackId`. This is how a goal sheet is
 *             approved; there is NO separate approve endpoint. CHANGE: the
 *             contract now ALLOWS optional goal rows here, so the employee's
 *             own goals are prefilled for the reviewer to score.
 *   create  → POST /employee with `userId`. Standalone feedback nobody asked
 *             for. Optional goal rows; weightage is all-or-nothing.
 *   edit    → PUT /employee/{id}. The employee and the approval link can never
 *             change, so neither is sent.
 */
export type ManagerFeedbackMode = "approve" | "create" | "edit";

/** The person the feedback is about. */
export interface FeedbackTarget {
  userId: number;
  name: string;
}

export interface ManagerFeedbackFormProps {
  mode: ManagerFeedbackMode;
  employee: FeedbackTarget;
  /** The SUBMITTED sheet being approved — required when mode is "approve". */
  selfFeedback?: Feedback | null;
  /** The record being edited — required when mode is "edit". */
  initialValue?: Feedback | null;
  areas: KraArea[];
  onSaved: (feedback: Feedback, message: string) => void;
  onClose: () => void;
}

const ManagerFeedbackForm: React.FC<ManagerFeedbackFormProps> = ({
  mode,
  employee,
  selfFeedback,
  initialValue,
  areas,
  onSaved,
  onClose,
}) => {
  const isApprove = mode === "approve";
  const isEdit = mode === "edit";

  const quarterOptions = useMemo(() => getSelectableQuarters(), []);

  const [selectedQuarter, setSelectedQuarter] = useState<QuarterRef>(() => {
    if (selfFeedback) return { year: selfFeedback.year, quarter: selfFeedback.quarter };
    if (initialValue) return { year: initialValue.year, quarter: initialValue.quarter };
    return quarterOptions[0];
  });

  const [overallComment, setOverallComment] = useState(initialValue?.overallComment ?? "");
  const [overallRating, setOverallRating] = useState<number | null>(
    initialValue?.overallRating ?? null,
  );
  /**
   * CHANGE: approving a sheet used to mount no row editor at all. It now starts
   * from the EMPLOYEE's goals — same areas, objectives and weightages — with the
   * comment and rating blank, because on an approving record those two belong
   * to the reviewer.
   */
  const [rows, setRows] = useState<GoalRowDraft[]>(() =>
    isApprove ? toApproverRowDrafts(selfFeedback?.items) : toGoalRowDrafts(initialValue?.items),
  );

  /**
   * CHANGE: new. Manager feedback is PRIVATE by default, so this starts OFF for
   * a new record and carries the saved value when editing one.
   */
  const [visibleToEmployee, setVisibleToEmployee] = useState<boolean>(
    initialValue?.visibleToEmployee ?? false,
  );

  /**
   * CHANGE: the per-goal grid is OPT-IN and OFF by default. Approving a sheet
   * is normally just a comment, and showing five prefilled rows made the common
   * case long and the grid look compulsory.
   *
   * NOTE: the exception is a record that ALREADY carries rows. `items` is
   * replaced on every save, so an unticked, unseen grid would delete the
   * author's own rows behind their back — such a record opens with it on.
   */
  const [showGoalRows, setShowGoalRows] = useState<boolean>(
    !isApprove && (initialValue?.items?.length ?? 0) > 0,
  );

  const [saving, setSaving] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [validation, setValidation] = useState(() => validateManagerRows({ rows: [] }));
  const [apiError, setApiError] = useState("");

  const totalWeightage = useMemo(() => calculateTotalWeightage(rows), [rows]);
  const hasAnyWeightage = rows.some((row) => row.weightage.trim() !== "");

  // ── Row editing ───────────────────────────────────────────────────────────

  const updateRow = (uid: string, patch: Partial<GoalRowDraft>) => {
    setRows((previous) => previous.map((row) => (row.uid === uid ? { ...row, ...patch } : row)));
    setApiError("");
  };

  const handleAreaChange = (uid: string, areaId: number | null) => {
    updateRow(uid, {
      areaId,
      objectiveId: null,
      savedAreaName: undefined,
      savedObjectiveName: undefined,
    });
  };

  const addRow = () => {
    if (!canAddGoalRow(rows)) return;
    setRows((previous) => [...previous, createEmptyGoalRow()]);
  };

  const removeRow = (uid: string) => {
    setRows((previous) => previous.filter((row) => row.uid !== uid));
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const trimmedComment = overallComment.trim();
    const nextCommentError = trimmedComment === "" ? "A comment is required." : "";
    // Hidden rows are not sent, so they must not be validated either —
    // otherwise a prefilled row could block a save the user cannot even see.
    const rowResult = showGoalRows
      ? validateManagerRows({ rows })
      : { rowErrors: {}, formError: "", isValid: true };

    setCommentError(nextCommentError);
    setValidation(rowResult);
    setApiError("");

    if (nextCommentError || !rowResult.isValid) return;

    setSaving(true);

    try {
      let response;

      if (isApprove && selfFeedback) {
        // Approval: the employee and quarter come from the sheet itself, so
        // nothing else is sent — anything mismatched would be a 400.
        response = await feedbackManager.approveSelfFeedback({
          selfFeedbackId: selfFeedback.id,
          overallComment: trimmedComment,
          overallRating,
          // Empty when the grid is off; the manager drops an empty list rather
          // than sending it, so approval stays a plain comment.
          items: showGoalRows ? toItemRequests(rows) : [],
          visibleToEmployee,
        });
      } else {
        const payload: ManagerFeedbackRequest = {
          overallComment: trimmedComment,
          overallRating,
          year: selectedQuarter.year,
          quarter: selectedQuarter.quarter,
          // [] on PUT clears the rows, which is what un-ticking the box means.
          items: showGoalRows ? toItemRequests(rows) : [],
          visibleToEmployee,
        };

        if (isEdit && initialValue) {
          response = await feedbackManager.updateManagerFeedback(initialValue.id, payload);
        } else {
          payload.userId = employee.userId;
          response = await feedbackManager.createManagerFeedback(payload);
        }
      }

      const period = formatPeriod(selectedQuarter);
      onSaved(
        response.data,
        isApprove
          ? `${employee.name}'s ${period} self feedback approved`
          : isEdit
            ? "Feedback updated successfully"
            : `Feedback added for ${employee.name}`,
      );
    } catch (error) {
      setApiError(getApiErrorMessage(error, "Failed to save feedback"));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">
      {/* Context strip — who and which quarter. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg bg-gray-50 px-3 py-2.5 dark:bg-gray-700/40">
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">About</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {employee.name}
          </span>
        </div>

        {isApprove || isEdit ? (
          <div className="flex items-baseline gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Period</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {formatPeriod(selectedQuarter)}
            </span>
            {/* The months this quarter covers, muted so it reads as a hint
                rather than a second value. */}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {getQuarterMonths(selectedQuarter.quarter)}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <label
              className="text-xs text-gray-500 dark:text-gray-400"
              htmlFor="manager-feedback-quarter"
            >
              Period
            </label>
            <select
              id="manager-feedback-quarter"
              value={quarterKey(selectedQuarter)}
              disabled={saving}
              onChange={(e) => {
                const parsed = parseQuarterKey(e.target.value);
                if (parsed) setSelectedQuarter(parsed);
              }}
              className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {quarterOptions.map((option) => (
                <option key={quarterKey(option)} value={quarterKey(option)}>
                  {/* An <option> can hold only text, so the months go inline
                      here rather than in a muted sibling. This is the surface
                      where the quarter is actually CHOSEN, so it matters most. */}
                  {formatPeriodWithMonths(option)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* On approval, show what is being approved so the reviewer has context. */}
      {isApprove && selfFeedback && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs dark:border-blue-800 dark:bg-blue-900/20">
          <p className="font-medium text-blue-900 dark:text-blue-200">
            Approving {employee.name}&apos;s goal sheet — {selfFeedback.items.length} goal
            {selfFeedback.items.length === 1 ? "" : "s"}, {selfFeedback.totalWeightage}% weightage.
          </p>
          <p className="mt-1 text-blue-700 dark:text-blue-300">
            Your comment is all that is needed. Tick &quot;Score each goal&quot; below if you
            also want to rate their goals one by one. Saving approves the sheet: there is no
            separate approve step, and no reject, so raise anything that needs changing with
            {employee.name} directly.
          </p>
        </div>
      )}

      {apiError && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-error dark:bg-red-900/20">
          {apiError}
        </div>
      )}
      {validation.formError && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          {validation.formError}
        </div>
      )}

      {/* ── Comment + rating ────────────────────────────────────────────── */}
      {/* NOTE: plain `flex flex-wrap` with fixed/min widths — deliberately NO
          `base sm:override` pair. In this project's built CSS the `sm:` block
          is emitted BEFORE the base utilities, so a base class always wins and
          a responsive override silently does nothing (that is what collapsed
          this row: `w-full` beat `sm:w-36`, and `shrink-0` stopped it giving
          the space back). Wrapping handles narrow screens instead. */}
      <div className="flex flex-wrap items-start gap-2.5">
        <div className="min-w-[18rem] flex-1">
          <label className={FIELD_LABEL_CLS} htmlFor="manager-overall-comment">
            Comment <span className="text-error">*</span>
          </label>
          <textarea
            id="manager-overall-comment"
            rows={3}
            value={overallComment}
            disabled={saving}
            placeholder={
              isApprove
                ? "Your view of this quarter, and what to focus on next"
                : "What this person did well, and where to grow"
            }
            onChange={(e) => {
              setOverallComment(e.target.value);
              setCommentError("");
            }}
            className={`${fieldCls(!!commentError)} resize-y`}
          />
          {commentError && <p className={FIELD_ERROR_CLS}>{commentError}</p>}
        </div>
        <div className="w-36 shrink-0">
          <label className={FIELD_LABEL_CLS} htmlFor="manager-overall-rating">
            Rating
          </label>
          <RatingSelect
            id="manager-overall-rating"
            value={overallRating}
            disabled={saving}
            onChange={setOverallRating}
          />
        </div>
      </div>

      {/* ── Goal rows — opt-in ──────────────────────────────────────────── */}
      <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={showGoalRows}
              disabled={saving}
              onChange={(e) => setShowGoalRows(e.target.checked)}
              className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed dark:border-gray-500 dark:bg-gray-600 dark:focus:ring-blue-600"
            />
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              {isApprove ? "Score each goal" : "Add goal rows"}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {isApprove
                ? "Optional — your rating and comment per goal"
                : "Optional — give every row a weightage, or none"}
            </span>
          </label>

          {showGoalRows && hasAnyWeightage && (
            <div className="min-w-[180px]">
              <WeightageMeter total={totalWeightage} />
            </div>
          )}
        </div>

        {showGoalRows && (
          <>
            <div className="flex flex-col gap-2.5">
              {rows.map((row, index) => {
                const errors = validation.rowErrors[row.uid] ?? {};
                const objectives = getObjectiveOptions({
                  areas,
                  areaId: row.areaId,
                  rows,
                  currentRowUid: row.uid,
                });

                return (
                  <div
                    key={row.uid}
                    className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700"
                  >
                    {/* Same reason as above: no 12-column grid whose `sm:` spans
                      never take effect, leaving every field stacked. */}
                    <div className="flex flex-wrap items-start gap-2.5">
                      <div className="min-w-[11rem] flex-1">
                        <label className={FIELD_LABEL_CLS} htmlFor={`${row.uid}-mgr-area`}>
                          Area
                        </label>
                        {/* CHANGE: shared component. This select previously
                          dropped a since-deactivated area entirely, so an
                          approving reviewer saw a blank required-looking field
                          where the employee had picked a retired one. */}
                        <KraAreaSelect
                          id={`${row.uid}-mgr-area`}
                          value={row.areaId}
                          areas={areas}
                          savedName={row.savedAreaName}
                          disabled={saving}
                          hasError={!!errors.areaId}
                          onChange={(areaId) => handleAreaChange(row.uid, areaId)}
                        />
                        {errors.areaId && <p className={FIELD_ERROR_CLS}>{errors.areaId}</p>}
                      </div>

                      <div className="min-w-[13rem] flex-1">
                        <label className={FIELD_LABEL_CLS} htmlFor={`${row.uid}-mgr-objective`}>
                          Objective
                        </label>
                        <KraObjectiveSelect
                          id={`${row.uid}-mgr-objective`}
                          value={row.objectiveId}
                          objectives={objectives}
                          areaChosen={row.areaId !== null}
                          savedName={row.savedObjectiveName}
                          disabled={saving}
                          hasError={!!errors.objectiveId}
                          onChange={(objectiveId) => updateRow(row.uid, { objectiveId })}
                        />
                        {errors.objectiveId && <p className={FIELD_ERROR_CLS}>{errors.objectiveId}</p>}
                      </div>

                      <div className="min-w-[13rem] flex-1">
                        <label className={FIELD_LABEL_CLS} htmlFor={`${row.uid}-mgr-achievement`}>
                          Achievement
                        </label>
                        <input
                          id={`${row.uid}-mgr-achievement`}
                          type="text"
                          value={row.achievement}
                          maxLength={MAX_ACHIEVEMENT_LENGTH}
                          disabled={saving}
                          onChange={(e) => updateRow(row.uid, { achievement: e.target.value })}
                          className={fieldCls(!!errors.achievement)}
                        />
                        {errors.achievement && <p className={FIELD_ERROR_CLS}>{errors.achievement}</p>}
                      </div>

                      <div className="w-24 shrink-0">
                        <label className={FIELD_LABEL_CLS} htmlFor={`${row.uid}-mgr-weightage`}>
                          Wt %
                        </label>
                        <input
                          id={`${row.uid}-mgr-weightage`}
                          type="number"
                          min={1}
                          max={100}
                          step={1}
                          value={row.weightage}
                          disabled={saving}
                          onChange={(e) => updateRow(row.uid, { weightage: e.target.value })}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          className={fieldCls(!!errors.weightage)}
                        />
                        {errors.weightage && <p className={FIELD_ERROR_CLS}>{errors.weightage}</p>}
                      </div>

                      <div className="flex w-36 shrink-0 items-end gap-1">
                        <div className="flex-1">
                          <label className={FIELD_LABEL_CLS} htmlFor={`${row.uid}-mgr-rating`}>
                            Rating
                          </label>
                          <RatingSelect
                            id={`${row.uid}-mgr-rating`}
                            value={row.rating}
                            disabled={saving}
                            onChange={(value) => updateRow(row.uid, { rating: value })}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeRow(row.uid)}
                          disabled={saving}
                          title="Remove this row"
                          aria-label={`Remove goal row ${index + 1}`}
                          className="mb-1.5 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-error disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900/20"
                        >
                          <FaRegTrashAlt size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-2">
              <HeaderButtons
                type="button"
                variant="tertiary"
                size="small"
                onClick={addRow}
                disabled={saving || !canAddGoalRow(rows)}
                icon={<FaPlus size={11} />}
              >
                Add row
              </HeaderButtons>
              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                {rows.length} of {MAX_GOAL_ROWS}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3 dark:border-gray-700">
        {/* CHANGE: new. Off by default — the contract keeps manager feedback
            private unless the author says otherwise, so the employee sees
            nothing until this is on. */}
        <Toggle
          id="manager-feedback-visible"
          checked={visibleToEmployee}
          onChange={setVisibleToEmployee}
          disabled={saving}
          label="Share with employee"
          hint={
            visibleToEmployee
              ? `${employee.name} will see this and be notified`
              : `Private to you and admins — ${employee.name} will not see it`
          }
        />

        <div className="flex flex-wrap items-center gap-3">
          {/* Same buttons as the goal-sheet screen and SalaryStructureFormPage. */}
          <Button type="button" onClick={onClose} disabled={saving} className={"!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95"}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saving}
            disabled={saving}
            /* CHANGE: the blue was hand-rolled here with !important, duplicating
             the brand colour. components/Button now supplies it from the
             `primary` theme token, so only the disabled state is overridden. */
            className={"disabled:!opacity-50 disabled:!cursor-not-allowed"}
          >
            {isApprove ? "Approve with Review" : isEdit ? "Save changes" : "Add Review"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManagerFeedbackForm;
